import * as Sentry from '@sentry/react'
import { message } from 'antd/lib';
import axios, {
    AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig, 
} from 'axios'

import { firebaseAuth } from '@/firestore/config.ts';

// Extend Window interface to include Auth0 client
declare global {
    interface Window {
        auth0Client?: {
            logout: (options?: { returnTo?: string }) => Promise<void>;
        };
    }
}

interface CustomAxiosRequestConfig<T = undefined> extends AxiosRequestConfig<T> {
    meta?: {
        /**
         * Alternative message instead of default backend message
         */
        errorToast?: string
    }
}

// Extend InternalAxiosRequestConfig to include our meta property
interface CustomInternalAxiosRequestConfig extends InternalAxiosRequestConfig {
    meta?: {
        errorToast?: string
    }
}

export const isAuthError = (error: AxiosError): boolean => {
    return (
        error.response?.status === 401
    )
}

// Handle authentication failure with proper logout from Firebase and Auth0
const handleAuthFailure = async () => {
    console.log('Auth error detected, performing complete logout');
    
    try {
        // 1. Log out from Firebase
        if (firebaseAuth.currentUser) {
            await firebaseAuth.signOut();
            console.log('Successfully logged out from Firebase');
        }
      
        // Alternative: Redirect to Auth0 logout endpoint directly
        const auth0Domain = import.meta.env.VITE_AUTH0_DOMAIN;
        const auth0ClientId = import.meta.env.VITE_AUTH0_CLIENT_ID;
        const returnToUrl = encodeURIComponent(window.location.origin);
            
        // Redirect to Auth0 logout endpoint
        window.location.href = `https://${auth0Domain}/v2/logout?client_id=${auth0ClientId}&returnTo=${returnToUrl}`;
        return; // Return early as we're redirecting
    } catch (error) {
        console.error('Error during authentication logout process:', error);
        Sentry.captureException(error);
    }
}

// TODO: Add custom error message
axios.defaults.xsrfHeaderName = 'X-CSRFToken'
axios.defaults.xsrfCookieName = 'csrftoken'
axios.defaults.withCredentials = true
axios.defaults.baseURL = '/api'
axios.defaults.timeout = 30000 // 30 seconds timeout

// Fix the type in the request interceptor
axios.interceptors.request.use((config: CustomInternalAxiosRequestConfig) => {
    if (!config.meta) {
        config.meta = {};
    }
    return config;
})

axios.interceptors.response.use(
    response => {
        return response
    },
    // TODO: Refactore this error handling. Looks really messy now.

    error => {
        const toastMessage = error.config?.meta?.errorToast
        const errorMessage = error.message?.length ? error.message : 'Request failed'

        const isAuth0Error = errorMessage.includes('Missing Refresh Token')
        const isNetworkError = errorMessage === 'Network Error' && error?.isAxiosError
        const isAuthenticationError = isAuthError(error)
        
        Sentry.withScope((scope) => {
            scope.setTag('httpServiceError', true)
            scope.setTag('Auth0RefreshTokenProblem', isAuth0Error)
            scope.setTag('isAuthenticationError', isAuthenticationError)
            scope.setContext('errorInfo', {
                toastMessage,
                isAxiosError: JSON.stringify(!!error?.isAxiosError),
                responseConfig: JSON.stringify(error?.response?.config),
                responseData: JSON.stringify(error?.response?.data),
                responseStatus: error?.response?.status,
            })
            if (isNetworkError) {
                scope.setTag('isNetworkError', isNetworkError)
            }
        })

        if (isAuth0Error || isAuthenticationError) {
            // Handle authentication failure with complete logout
            message.error('Your session has expired. You will be logged out.');
            // Use void to handle the promise without waiting (avoid blocking UI)
            void handleAuthFailure();
        } else {
            if (toastMessage) {
                message.error(toastMessage)
            }
        }

        throw error
    })

/**
 * Alternative extended interface for axios methods
 */
interface CustomAxiosStatic extends AxiosInstance {
    get: <T = any, R = AxiosResponse<T>, D = any>(url: string, config?: CustomAxiosRequestConfig<D>) => Promise<R>
    delete: <T = any, R = AxiosResponse<T>, D = any>(url: string, config?: CustomAxiosRequestConfig<D>) => Promise<R>
    post: <T = any, R = AxiosResponse<T>, D = any>(url: string, data?: D, config?: CustomAxiosRequestConfig<D>) => Promise<R>
    patch: <T = any, R = AxiosResponse<T>, D = any>(url: string, data?: D, config?: CustomAxiosRequestConfig<D>) => Promise<R>
}

/**
 * Wrappers with extra parameters
 */
const http: Partial<CustomAxiosStatic> = {
    ...axios,
    get: <T = any, R = AxiosResponse<T>, D = any>(url: string, config?: CustomAxiosRequestConfig<D>): Promise<R> => {
        return axios.get<T, R>(url, config)
    },
    post: <T = any, R = AxiosResponse<T>, D = any>(url: string, data?: D, config?: CustomAxiosRequestConfig<D>): Promise<R> => {
        return axios.post<T, R>(url, data, config)
    },
    patch: <T = any, R = AxiosResponse<T>, D = any>(url: string, data?: D, config?: CustomAxiosRequestConfig<D>): Promise<R> => {
        return axios.patch<T, R>(url, data, config)
    },
    delete: <T = any, R = AxiosResponse<T>, D = any>(url: string, config?: CustomAxiosRequestConfig<D>): Promise<R> => {
        return axios.delete<T, R>(url, config)
    },
}
export default http
